//jan20 BillingContext.js:
/* ------------------------------------------------------------------
 * BillingContext.js — consolidated (2025‑06‑17)
 * ------------------------------------------------------------------
 * Combines the real‑time Firestore listener + portal helpers that were
 * in the ‘BillingContext.js’ *and* the plan/checkout/seat utilities that
 * were in the legacy ‘BillingsContext.js’.  All billing logic now lives
 * in ONE provider & ONE context (BillingsContext).
 * ------------------------------------------------------------------*/

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";

/* Firebase + Stripe helpers ------------------------------------------------*/
import { httpsCallable }       from "firebase/functions";
import { functions, db }       from "../firebase";
import { doc, onSnapshot }     from "firebase/firestore";

/* Plan catalog (single source of truth) ----------------------------------- */
import { planConfigs, priceIdToPlanKey } from "plan-catalog";

/* App‑level hooks -----------------------------------------------------------*/
import { useAuth }          from "./AuthContext";
import { useOrganization2 } from "./OrganizationContext2";
import { useLoading }       from "./LoadingContext";
import dayjs from "dayjs";

/* ------------------------------------------------------------------ */
/* Runtime URLs – used by Stripe success / cancel callbacks            */
/* ------------------------------------------------------------------ */
const CHECKOUT_RESULT_URL =
  window.location.origin + "/billing/checkout-result";

/* ------------------------------------------------------------------
 * Short-lived invoice-details cache
 *
 * Why:
 * - Pending-invoice reconciliation can ask for the same invoice details
 *   multiple times in a very short window while Firestore snapshots and
 *   provider state settle.
 * - We want to keep backend authority, but avoid redundant callable reads
 *   for the same invoice id during the same review/payment cycle.
 *
 * Enterprise rule:
 * - Cache only short-lived invoice detail reads.
 * - Do NOT cache mutation results here.
 * ------------------------------------------------------------------ */
const INVOICE_DETAILS_CACHE_TTL_MS = 30 * 1000;

/* -------------------------------------------------------------------------*/
const BillingsContext = createContext();
export const useBillings = () => useContext(BillingsContext);

/* -------------------------------------------------------------------------
 * Gold-standard pending-change runtime state
 *
 * Why:
 * - BillingContext should be the single owner of immediate pending-change
 *   runtime classification.
 * - The provider should expose one normalized object that downstream UI can
 *   trust instead of forcing each consumer to infer meaning from three
 *   loosely-related fields.
 *
 * IMPORTANT:
 * - This aligns with your current Firestore schema and stripeFunctions.js:
 *     • organizations/{orgId}.pendingStripeChange
 *     • getInvoiceDetails()
 *     • cancelPendingBillingChange()
 * - We keep the legacy exported fields temporarily for compatibility while
 *   migrating the drawer/page to the new provider contract.
 * ---------------------------------------------------------------------- */
const EMPTY_PENDING_CHANGE_STATE = Object.freeze({
  kind: "none", // none | checking | resume_payment | transitional | stale | error
  pendingChange: null,
  invoiceId: null,
  invoiceUrl: null,
  invoiceStatus: null,
  paymentStatus: null,
  invoiceDetails: null,
  invoiceClassification: "idle",
  subscriptionId: null,
  nextSeatCount: null,
  nextPriceId: null,
  nextPlanName: null,
  nextPlanKey: null,
  nextInterval: null,
  nextRenewalDate: null,
  requestedAt: null,
  canResumePayment: false,
});

const createEmptyPendingChangeState = () => ({
  ...EMPTY_PENDING_CHANGE_STATE,
});

const buildPendingChangeCandidateState = (pendingChange) => {
  const nextSeatCount =
    Number.isFinite(Number(pendingChange?.nextSeatCount)) ?
      Number(pendingChange.nextSeatCount) :
      null;

  return {
    kind: "checking",
    pendingChange: pendingChange || null,
    invoiceId: pendingChange?.invoiceId || null,
    invoiceUrl: null,
    invoiceStatus: null,
    paymentStatus: null,
    invoiceDetails: null,
    invoiceClassification: "checking",
    subscriptionId: pendingChange?.subscriptionId || null,
    nextSeatCount,
    nextPriceId: pendingChange?.nextPriceId || null,
    nextPlanName: pendingChange?.nextPlanName || null,
    nextPlanKey: pendingChange?.nextPlanKey || null,
    nextInterval: pendingChange?.nextInterval || null,
    nextRenewalDate: pendingChange?.nextRenewalDate || null,
    requestedAt: pendingChange?.requestedAt || null,
    canResumePayment: false,
  };
};

const buildResolvedPendingChangeState = ({
  pendingChange,
  invoiceDetails,
  classification,
}) => {
  const base = buildPendingChangeCandidateState(pendingChange);

  const normalizedClassification =
    classification === "actionable" ||
    classification === "transitional" ||
    classification === "terminal" ||
    classification === "unknown" ?
      classification :
      "error";

  const kind =
    normalizedClassification === "actionable" ?
      "resume_payment" :
      normalizedClassification === "transitional" ?
        "transitional" :
        normalizedClassification === "terminal" ||
        normalizedClassification === "unknown" ?
          "stale" :
          "error";

  return {
    ...base,
    kind,
    invoiceUrl:
      typeof invoiceDetails?.invoiceUrl === "string" &&
      invoiceDetails.invoiceUrl.trim() !== "" ?
        invoiceDetails.invoiceUrl.trim() :
        null,
    invoiceStatus:
      invoiceDetails?.invoiceStatus ||
      invoiceDetails?.paymentStatus ||
      null,
    paymentStatus:
      invoiceDetails?.paymentStatus ||
      invoiceDetails?.invoiceStatus ||
      null,
    invoiceDetails: invoiceDetails || null,
    invoiceClassification: normalizedClassification,
    canResumePayment: kind === "resume_payment",
  };
};

const mapPendingChangeStateKindToLegacyStatus = (kind) => {
  if (kind === "none") return "idle";
  if (kind === "resume_payment") return "actionable";
  if (kind === "checking") return "checking";
  if (kind === "transitional") return "transitional";
  if (kind === "stale") return "stale";
  if (kind === "error") return "error";
  return "idle";
};

/* -------------------------------------------------------------------------*/
export const BillingsProvider = ({ children }) => {
  /* ───────────────────────  base dependencies  ─────────────────────── */
  const { initialized }            = useAuth();
  const { organization, setOrganization } = useOrganization2();
  const { setLoading }             = useLoading();

  /* ───────────────────────────  state  ─────────────────────────────── */
  const [isOrgReady, setIsOrgReady]       = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);   // list (legacy)
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [pendingBillingChange, setPendingBillingChange] = useState(null);

  /* ────────────────────────────────────────────────────────────────
   * Gold-standard normalized pending-change provider state
   *
   * Why:
   * - This becomes the single provider-owned runtime state machine output
   *   for immediate pending billing changes.
   * - The rest of the app should eventually read THIS instead of trying to
   *   infer billing meaning from separate fields.
   *
   * Compatibility note:
   * - We keep pendingBillingChange, pendingBillingChangeStatus, and
   *   pendingBillingChangeInvoiceDetails temporarily while the rest of the
   *   UI is migrated.
   * ---------------------------------------------------------------- */
  const [pendingChangeState, setPendingChangeState] = useState(
    createEmptyPendingChangeState(),
  );

  const [invoices, setInvoices]     = useState([]);
  const [plans, setPlans]           = useState([]);
  const [hasValidPaymentMethod, setHasValidPaymentMethod] = useState(null);
  // flips to true after the first Firestore snapshot resolves
  const [billingLoaded, setBillingLoaded] = useState(false);
  const [showNavUpgrade,  setShowNavUpgrade]  = useState(false);
  const [reconciling, setReconciling] = useState(false);

  /* ────────────────────────────────────────────────────────────────
   * Legacy compatibility exports
   *
   * Why:
   * - Existing consumers still read:
   *     • pendingBillingChange
   *     • pendingBillingChangeStatus
   *     • pendingBillingChangeInvoiceDetails
   * - During this refactor, these are treated as compatibility fields
   *   derived from the normalized pendingChangeState provider state.
   *
   * IMPORTANT:
   * - New code should prefer pendingChangeState.
   * ---------------------------------------------------------------- */
  const [pendingBillingChangeStatus, setPendingBillingChangeStatus] =
    useState("idle");
  const [pendingBillingChangeInvoiceDetails, setPendingBillingChangeInvoiceDetails] =
    useState(null);

  /* ────────────────────────────────────────────────────────────────
   * Snapshot guards
   *  • Avoid stale-closure checks (billingLoaded) inside onSnapshot.
   *  • Avoid global window flags that can behave oddly with HMR/dev.
   *  • Refs do NOT trigger re-renders and do NOT require deps changes.
   * ──────────────────────────────────────────────────────────────── */
  const billingLoadedRef = useRef(false);
  const billingSyncTriggeredRef = useRef(false);
  const currentSubscriptionRef = useRef(null);

  /* ────────────────────────────────────────────────────────────────
   * Pending-change reconciliation guards
   *
   * - We only want the most recent pending-change reconciliation request
   *   to be allowed to update provider state.
   * - Firestore can emit several snapshots in quick succession while a
   *   hosted invoice is being created / updated / voided.
   * ──────────────────────────────────────────────────────────────── */
  const pendingChangeReconSeqRef = useRef(0);
  const activePendingChangeReconRef = useRef(0);

  /* ────────────────────────────────────────────────────────────────
   * Pending-change reconciliation memory
   *
   * Why:
   * - Even after semantic normalization, Firestore may still emit the same
   *   actionable pending invoice state multiple times while Stripe-hosted
   *   flows settle.
   * - We want BillingContext to remember the last invoice id it already
   *   reconciled so we do not re-fetch invoice details unnecessarily.
   * ---------------------------------------------------------------- */
  const lastReconciledPendingInvoiceIdRef = useRef(null);
  const lastReconciledPendingInvoiceStatusRef = useRef("idle");
  const lastPendingReconciliationKeyRef = useRef(null);

  /* ---------------------------------------------------------------
   * Invoice-details read cache + in-flight dedupe
   *
   * Why:
   * - getInvoiceDetails() can be requested repeatedly for the same invoice
   *   while pendingChangeState transitions through checking/actionable/stale.
   * - We want BillingContext to reuse a recent authoritative response for
   *   the same invoice id instead of paying repeated callable latency.
   *
   * IMPORTANT:
   * - This cache is intentionally short-lived.
   * - It is only for invoice detail READS.
   * - It does not weaken backend authority because the source of truth is
   *   still the backend callable.
   * ------------------------------------------------------------- */
  const invoiceDetailsCacheRef = useRef(new Map());
  const invoiceDetailsInFlightRef = useRef(new Map());

  /* ───────────────────────────────
   * Keep the silent Navbar “Upgrade”
   * CTA in-sync with banner status.
   * ───────────────────────────────*/
  useEffect(() => {
    if (!currentSubscription) return;

    // 1) Hide CTA for paid / cancelled
    if (currentSubscription.status !== "trialing") {
      if (showNavUpgrade) setShowNavUpgrade(false);
      return;
    }

    // 2)  Trialing → compute days left
    const end = currentSubscription.trialEndsAt
      ? dayjs(currentSubscription.trialEndsAt)
      : null;
    if (!end?.isValid()) return;           // safety

    const daysLeft        = Math.max(0, end.diff(dayjs(), "day"));
    const bannerDismissed =
      sessionStorage.getItem("trialBannerDismissed") === "true";

    const shouldShow = bannerDismissed && daysLeft > 2;
    if (shouldShow !== showNavUpgrade) {
      setShowNavUpgrade(shouldShow);
    }
  }, [currentSubscription, showNavUpgrade]);

  /* ────────────────────────────────────────────────────────────────
   *  Helper: figure out *which* catalog entry this org is on.
   *  Accepts whatever we have on the org doc:
   *    • data.planName (may already be “standard”, “pro”, …)
   *    • OR data.priceId   (Stripe price → lookup via map)
   *  Returns: canonical key we use in planConfigs (*or* "unknown")
   * ────────────────────────────────────────────────────────────────*/
  const resolvePlanKey = (planKeyInDoc, planNameInDoc, priceIdInDoc) => {
    // 0) prefer canonical planKey written by the server
    if (planKeyInDoc && planConfigs[planKeyInDoc]) return planKeyInDoc;
    // 1) else try priceId → key map built from the catalog
    if (priceIdInDoc && priceIdToPlanKey[priceIdInDoc]) {
      return priceIdToPlanKey[priceIdInDoc];  // e.g. "standard"
    }
    // 2) last resort: accept planName when it matches a catalog key
    if (planConfigs[planNameInDoc]) return planNameInDoc;
    // 3) couldn’t map → fall back
    return "unknown";
  };

  /* ────────────────────────────────────────────────────────────────
   * Subscription snapshot equality guard
   *
   * Why:
   * - Firestore can emit fresh organization snapshots even when the
   *   billing values relevant to the pricing UI have not materially changed.
   * - BillingContext currently normalizes every snapshot into a brand-new
   *   currentSubscription object, which forces page-wide rerenders.
   * - We only want to update React state when meaningful subscription fields
   *   have actually changed.
   *
   * IMPORTANT:
   * - This does NOT change billing logic or Stripe behavior.
   * - It only avoids unnecessary state churn in the UI layer.
   * ---------------------------------------------------------------- */
  const areSubscriptionsMeaningfullyEqual = (prevSub, nextSub) => {
    if (!prevSub && !nextSub) return true;
    if (!prevSub || !nextSub) return false;

    return (
      prevSub.customerId === nextSub.customerId &&
      prevSub.planName === nextSub.planName &&
      prevSub.price === nextSub.price &&
      prevSub.priceId === nextSub.priceId &&
      prevSub.interval === nextSub.interval &&
      prevSub.seats === nextSub.seats &&
      prevSub.id === nextSub.id &&
      prevSub.stripeSubscriptionItemId === nextSub.stripeSubscriptionItemId &&
      prevSub.status === nextSub.status &&
      prevSub.rawStatus === nextSub.rawStatus &&
      prevSub.cancelAtPeriodEnd === nextSub.cancelAtPeriodEnd &&
      prevSub.currentPeriodEndMs === nextSub.currentPeriodEndMs &&
      prevSub.stillInPaidTerm === nextSub.stillInPaidTerm &&
      prevSub.renewalDate === nextSub.renewalDate &&
      prevSub.lastBilledAmount === nextSub.lastBilledAmount &&
      prevSub.hasValidPaymentMethod === nextSub.hasValidPaymentMethod &&
      prevSub.trialStartsAt === nextSub.trialStartsAt &&
      prevSub.trialEndsAt === nextSub.trialEndsAt &&
      JSON.stringify(prevSub.pendingStripeSchedule || null) ===
        JSON.stringify(nextSub.pendingStripeSchedule || null)
    );
  };

  /* ────────────────────────────────────────────────────────────────
   * Pending-change equality guard
   *
   * Why:
   * - Firestore can re-emit pendingStripeChange objects that are materially
   *   the same but differ in object identity or harmless field ordering.
   * - Using JSON.stringify() here is too noisy and causes repeated
   *   reconciliation work for the same logical pending change.
   *
   * IMPORTANT:
   * - Keep this focused on fields that matter to downstream billing UI.
   * - Do NOT include ephemeral object identity.
   * - This helper must be memoized because subscribeToOrgDoc depends on it.
   * ---------------------------------------------------------------- */
  const arePendingBillingChangesMeaningfullyEqual = useCallback((
    prevPendingChange,
    nextPendingChange,
  ) => {
    if (!prevPendingChange && !nextPendingChange) return true;
    if (!prevPendingChange || !nextPendingChange) return false;

    return (
      (prevPendingChange.subscriptionId || null) ===
        (nextPendingChange.subscriptionId || null) &&
      (prevPendingChange.invoiceId || null) ===
        (nextPendingChange.invoiceId || null) &&
      (
        Number.isFinite(Number(prevPendingChange.nextSeatCount)) ?
          Number(prevPendingChange.nextSeatCount) :
          null
      ) ===
        (
          Number.isFinite(Number(nextPendingChange.nextSeatCount)) ?
            Number(nextPendingChange.nextSeatCount) :
            null
        ) &&
      (prevPendingChange.nextPriceId || null) ===
        (nextPendingChange.nextPriceId || null) &&
      (prevPendingChange.requestedAt || null) ===
        (nextPendingChange.requestedAt || null)
    );
  }, []);

  /* ────────────────────────────────────────────────────────────────
   * Pending billing-change normalization
   *
   * Why:
   * - Firestore can briefly surface transitional pendingStripeChange values
   *   while Stripe invoice state is settling.
   * - The UI should not need to interpret every transient pending invoice
   *   state on its own.
   * - BillingContext is the correct ownership boundary for deciding whether a
   *   pending change is still actionable enough to expose to the app shell.
   *
   * IMPORTANT:
   * - This helper is intentionally synchronous and snapshot-only.
   * - It does NOT call Stripe or fetch invoice details.
   * - It only suppresses obviously stale / already-settled pending state.
   * ---------------------------------------------------------------- */
  const normalizePendingBillingChange = useCallback(({
    pendingStripeChange,
    subscriptionId,
    settledSeatCount,
    settledPriceId,
  }) => {
    const pending = pendingStripeChange || null;

    if (!pending) {
      return null;
    }

    const pendingSubscriptionId =
      pending.subscriptionId || null;

    const pendingInvoiceId =
      pending.invoiceId || null;

    const pendingNextSeatCount =
      Number.isFinite(Number(pending.nextSeatCount)) ?
        Number(pending.nextSeatCount) :
        null;

    const pendingNextPriceId =
      pending.nextPriceId || null;

    /* ------------------------------------------------------------
     * Must belong to the currently-settled subscription snapshot.
     * ---------------------------------------------------------- */
    if (
      pendingSubscriptionId &&
      subscriptionId &&
      pendingSubscriptionId !== subscriptionId
    ) {
      console.warn(
        "[BillingContext] Suppressing pending billing change " +
        "for a different subscription:",
        {
          pending,
          subscriptionId,
        },
      );
      return null;
    }

    /* ------------------------------------------------------------
     * Must have enough shape to be actionable.
     * ---------------------------------------------------------- */
    if (!pendingInvoiceId) {
      console.warn(
        "[BillingContext] Suppressing pending billing change " +
        "without invoiceId:",
        {
          pending,
        },
      );
      return null;
    }

    if (
      pendingNextSeatCount === null &&
      !pendingNextPriceId
    ) {
      console.warn(
        "[BillingContext] Suppressing pending billing change " +
        "without next state:",
        {
          pending,
        },
      );
      return null;
    }

    /* ------------------------------------------------------------
     * If the org snapshot already reflects the requested next state,
     * this pending change is no longer useful to expose to the UI.
     *
     * Seat-only case:
     * - nextSeatCount matches settled seatCount
     * - nextPriceId is absent OR matches settled priceId
     *
     * Plan/seat case:
     * - nextSeatCount matches settled seatCount (or omitted)
     * - nextPriceId matches settled priceId
     * ---------------------------------------------------------- */
    const settledSeatsMatch =
      pendingNextSeatCount === null ||
      pendingNextSeatCount === Number(settledSeatCount || 0);

    const settledPriceMatches =
      !pendingNextPriceId ||
      pendingNextPriceId === (settledPriceId || null);

    if (settledSeatsMatch && settledPriceMatches) {
      console.warn(
        "[BillingContext] Suppressing pending billing change " +
        "because settled subscription already matches requested next state:",
        {
          pending,
          settledSeatCount,
          settledPriceId,
        },
      );
      return null;
    }

    return pending;
  }, []);

  /* ────────────────────────────────────────────────────────────────
   * Pending invoice actionability helpers
   *
   * Why:
   * - normalizePendingBillingChange() is intentionally snapshot-only.
   * - These helpers let BillingContext perform a second-stage invoice
   *   reconciliation without moving Stripe-specific UI decisions into
   *   ReviewChangesDrawer2.
   * ──────────────────────────────────────────────────────────────── */
  const normalizeInvoiceUrl = (invoiceUrl) => {
    if (typeof invoiceUrl !== "string") return null;
    const trimmed = invoiceUrl.trim();
    return trimmed ? trimmed : null;
  };

  const classifyPendingInvoiceDetails = useCallback((invoiceDetails) => {
    const invoiceStatus =
      String(
          invoiceDetails?.invoiceStatus ||
          invoiceDetails?.paymentStatus ||
          "",
      ).toLowerCase().trim();

    const paymentIntentStatus =
      String(
          invoiceDetails?.paymentIntentStatus || "",
      ).toLowerCase().trim();

    const invoiceUrl = normalizeInvoiceUrl(invoiceDetails?.invoiceUrl);

    /* ------------------------------------------------------------
     * ACTIONABLE
     *
     * Why:
     * - A hosted invoice URL plus an open/unpaid invoice means the customer
     *   can usually resume payment directly.
     * - PaymentIntent states that require customer action should also be
     *   treated as actionable when the hosted invoice URL is available.
     * ---------------------------------------------------------- */
    if (
      invoiceUrl &&
      (
        invoiceStatus === "open" ||
        invoiceStatus === "unpaid" ||
        paymentIntentStatus === "requires_action" ||
        paymentIntentStatus === "requires_payment_method" ||
        paymentIntentStatus === "requires_confirmation"
      )
    ) {
      return "actionable";
    }

    /* ------------------------------------------------------------
     * TERMINAL
     *
     * Why:
     * - These states mean the pending invoice should no longer drive resume
     *   payment UI.
     * - The provider should suppress or clear this pending billing state.
     * ---------------------------------------------------------- */
    if (
      invoiceStatus === "paid" ||
      invoiceStatus === "void" ||
      invoiceStatus === "uncollectible" ||
      paymentIntentStatus === "succeeded" ||
      paymentIntentStatus === "canceled"
    ) {
      return "terminal";
    }

    /* ------------------------------------------------------------
     * TRANSITIONAL
     *
     * Why:
     * - These states usually mean Stripe is still stabilizing the invoice /
     *   payment lifecycle.
     * - The UI should avoid surfacing a stale resume-payment CTA too early.
     * ---------------------------------------------------------- */
    if (
      invoiceStatus === "draft" ||
      invoiceStatus === "pending" ||
      invoiceStatus === "open" ||
      invoiceStatus === "unpaid" ||
      paymentIntentStatus === "processing" ||
      paymentIntentStatus === "requires_action" ||
      paymentIntentStatus === "requires_payment_method" ||
      paymentIntentStatus === "requires_confirmation"
    ) {
      return "transitional";
    }

    if (!invoiceStatus && !paymentIntentStatus && !invoiceUrl) {
      return "unknown";
    }

    return "unknown";
  }, []);

  /* ────────────────────────────────────────────────────────────────
   * Pending-change suppression from reconciliation memory
   *
   * Why:
   * - After BillingContext has already reconciled a pending invoice, we
   *   should not keep re-exposing the same invoice back into app state if
   *   we already know it is stale / terminal.
   * - This is the provider-level stabilizer that prevents:
   *     snapshot -> expose pending -> reconcile stale -> hide pending
   *     -> snapshot re-emits same pending -> expose again -> repeat
   *
   * IMPORTANT:
   * - This does NOT replace normalizePendingBillingChange().
   * - It runs AFTER snapshot normalization.
   * - It uses only provider-owned memory from prior reconciliations.
   * ---------------------------------------------------------------- */
  const suppressPendingBillingChangeFromReconciledMemory = useCallback((
    pendingChange,
  ) => {
    const pending = pendingChange || null;
    if (!pending) return null;

    const pendingInvoiceId = pending.invoiceId || null;
    if (!pendingInvoiceId) return pending;

    const lastInvoiceId = lastReconciledPendingInvoiceIdRef.current || null;
    const lastInvoiceStatus =
      lastReconciledPendingInvoiceStatusRef.current || "idle";

    if (pendingInvoiceId !== lastInvoiceId) {
      return pending;
    }

    if (lastInvoiceStatus === "stale") {
      console.warn(
        "[BillingContext] Suppressing pending billing change from " +
        "reconciliation memory because this invoice was already classified " +
        "as non-actionable:",
        {
          pending,
          lastInvoiceId,
          lastInvoiceStatus,
        },
      );
      return null;
    }

    return pending;
  }, []);

  /* ────────────────────────────────────────────────────────────────
   * Sales-led helpers (NEW)
   *  • Prevent calling Stripe self-serve flows for contact-sales plans.
   *  • Normalize the CF error "CONTACT_SALES" into a clean redirect.
   * ──────────────────────────────────────────────────────────────── */
  const getContactSalesUrl = useCallback(() => {
    const enterprise = planConfigs?.enterprise || {};
    const urlFromCatalog = enterprise?.cta?.contact?.url || null;
    return urlFromCatalog || (window.location.origin + "/contact-sales");
  }, []);

  const isContactSalesPriceId = useCallback((priceId) => {
    if (!priceId) return false;
    const key = priceIdToPlanKey?.[priceId] || null;
    const cfg = key ? planConfigs?.[key] || {} : {};
    const selfServe = cfg?.availability?.selfServe !== false;
    const contact = cfg?.cta?.type === "contact_sales";
    const display = cfg?.pricingDisplay === "contact";
    return !selfServe || contact || display;
  }, []);

  const isContactSalesHttpsError = (err) => {
    const code = err?.code || "";
    const msg = (err?.message || "").toString();
    const isFailedPrecond = code === "functions/failed-precondition";
    const isContactMsg = msg.includes("CONTACT_SALES");
    return isFailedPrecond && isContactMsg;
  };

  const redirectToContactSales = useCallback(() => {
    const url = getContactSalesUrl();
    window.location.href = url;
  }, [getContactSalesUrl]);


//console.log("[DEBUG] planConfigs import came from:", planConfigs);
//console.trace('[DEBUG] ← imported here');

  /* ───────────────────────  org readiness  ─────────────────────────── */
  useEffect(() => {
    const ready = initialized && !!organization?.stripeCustomerId;
    if (ready !== isOrgReady) setIsOrgReady(ready);
  }, [initialized, organization?.stripeCustomerId, isOrgReady]);

  /* ────────────────────────────────────────────────────────────────
   * Reset snapshot guards when org context changes
   *  • Prevent stale "already loaded" flags if user changes orgs
   *  • Prevent stale "sync already triggered" flags after logout/login
   * ---------------------------------------------------------------- */
  useEffect(() => {
    // If org is missing or auth not initialized, reset guards.
    if (!initialized || !organization?.id) {
      billingLoadedRef.current = false;
      billingSyncTriggeredRef.current = false;
      return;
    }

    // New org id → reset one-time guards for the new org.
    billingLoadedRef.current = false;
    billingSyncTriggeredRef.current = false;

    // Also reset state so UI can show the correct loading sequence.
    setBillingLoaded(false);
  }, [initialized, organization?.id]);


  /* ────────────────  Manual sync (callable)  ─────────────────────────
   * Enterprise-grade reconciliation helper
   *
   * Supports:
   *  • default org-driven sync (existing in-app behavior)
   *  • explicit customerId-driven sync (post-Stripe return pages)
   *
   * Why:
   * - Result/receipt pages should not depend on OrganizationContext
   *   being fully hydrated before they can reconcile billing state.
   * - Passing an explicit Stripe customer ID makes the sync path
   *   deterministic after redirects from Stripe Checkout.
   * ---------------------------------------------------------------- */
const syncStripeSubscriptionDetails = useCallback(async ({
  stripeCustomerIdOverride = null,
} = {}) => {
  const resolvedStripeCustomerId =
    stripeCustomerIdOverride ||
    organization?.stripeCustomerId ||
    null;

  if (!resolvedStripeCustomerId) {
    console.warn(
      "[BillingContext] syncStripeSubscriptionDetails skipped: " +
      "missing stripeCustomerId",
    );
    return null;
  }

  setLoading(true, "BillingsContext");
  try {
    const callable = httpsCallable(
      functions,
      "syncStripeSubscriptionDetails",
    );

    const {data} = await callable({
      stripeCustomerId: resolvedStripeCustomerId,
    });

    if (!data?.success) {
      console.warn(
        "[BillingContext] syncStripeSubscriptionDetails returned " +
        "non-success result:",
        data,
      );
      return data || null;
    }

    const {
      subscriptionId,
      subscriptionStatus,
      stripeSubscriptionItemId,
      seatCount,
      planName,
      planKey,
      price,
      priceId,
      interval,
      renewalDate,
      cancelAtPeriodEnd,
      currentPeriodEnd,
      hasValidPaymentMethod,
      trialStartsAt,
      trialEndsAt,
    } = data;

    setOrganization((prev) => ({
      ...prev,
      subscriptionId,
      subscriptionStatus,
      stripeSubscriptionItemId,
      seatCount,
      planName,
      planKey: planKey || null,
      price: price != null ? String(price) : "0",
      priceId: priceId || null,
      interval,
      renewalDate,
      cancelAtPeriodEnd: Boolean(cancelAtPeriodEnd),
      currentPeriodEnd: currentPeriodEnd || null,
      hasValidPaymentMethod:
        typeof hasValidPaymentMethod === "boolean" ?
          hasValidPaymentMethod :
          prev?.hasValidPaymentMethod ?? null,
      trialStartsAt: trialStartsAt || null,
      trialEndsAt: trialEndsAt || null,
      updatedAt: new Date().toISOString(),
    }));

    return data;
  } catch (err) {
    console.error("[BillingContext] syncStripeSubscriptionDetails", err);
    throw err;
  } finally {
    setLoading(false, "BillingsContext");
  }
}, [organization?.stripeCustomerId, setOrganization, setLoading]);

  /* ─────────────────────  Firestore real‑time listener  ─────────────── */
  const subscribeToOrgDoc = useCallback(() => {
    if (!isOrgReady || !organization?.id) return undefined;

    const unsubscribe = onSnapshot(doc(db, "organizations", organization.id), snap => {
      if (!snap.exists()) return;
      const data = snap.data();

      /*figure out canonicalKey + human name ---------------------- */
      const canonicalKey = resolvePlanKey(
        data.planKey,
        data.planName,
        data.priceId
      );

      const displayName  =
        canonicalKey !== "unknown"
          ? canonicalKey.charAt(0).toUpperCase() + canonicalKey.slice(1)
          : data.planName || "Unknown Plan";

//      console.log("[BillingContext] Plan resolved →", {
//        canonicalKey,
//        displayName,
//        fromDoc: data.planName,
//        priceId: data.priceId,
//      });
      /* -------------------------------------------------------------- */

      // timestamp → ISO
      const renewalDateISO = data.renewalDate
        ? new Date(data.renewalDate).toISOString()
        : null;

      const cancelAtPeriodEnd = Boolean(data.cancelAtPeriodEnd);

      const toMs = (val) => {
        if (!val) return null;

        // Firestore Timestamp object (plain data)
        if (val.seconds != null) return val.seconds * 1000;

        // Firestore Timestamp instance (sdk)
        if (typeof val.toDate === "function") return val.toDate().getTime();

        // JS Date
        if (val instanceof Date) return val.getTime();

        // ISO string (or other date-like string)
        if (typeof val === "string") return new Date(val).getTime();

        return null;
      };

      const currentPeriodEndMs = toMs(data.currentPeriodEnd);

      const nowMs = Date.now();


      const stillInPaidTerm =
        cancelAtPeriodEnd &&
        Number.isFinite(currentPeriodEndMs) &&
        nowMs < currentPeriodEndMs;

      const rawStatus = data.subscriptionStatus ?? null;

      const effectiveStatus =
        rawStatus === "canceled" && stillInPaidTerm ? "active" : rawStatus;

      /* helper to normalise Firestore Timestamp vs stored ISO string */
      const toISO = (val) => {
        if (!val) return null;
        // New flow stores ISO strings ─ just return them
        if (typeof val === "string") return new Date(val).toISOString();
        // Back-compat: FieldValue.Timestamp { seconds, nanoseconds }
        if (val.seconds != null) return new Date(val.seconds * 1000).toISOString();
        return null;
      };

      const rawPendingStripeChange = data.pendingStripeChange || null;

      const normalizedPendingStripeChange = normalizePendingBillingChange({
        pendingStripeChange: rawPendingStripeChange,
        subscriptionId: data.subscriptionId || null,
        settledSeatCount: data.seatCount || 0,
        settledPriceId: data.priceId || null,
      });

      const pendingStripeChange =
        suppressPendingBillingChangeFromReconciledMemory(
          normalizedPendingStripeChange,
        );

      const nextPendingChangeStateCandidate =
        pendingStripeChange ?
          buildPendingChangeCandidateState(pendingStripeChange) :
          createEmptyPendingChangeState();

      const nextSub = {
        customerId               : data.stripeCustomerId,
        planName                 : displayName,
        price                    : data.price?.toString() ?? "0",
        priceId                  : data.priceId,
        interval                 : data.interval,
        seats                    : data.seatCount,
        id                       : data.subscriptionId, // keep old API
        stripeSubscriptionItemId : data.stripeSubscriptionItemId,
        status                   : effectiveStatus,
        rawStatus                : rawStatus,
        cancelAtPeriodEnd        : Boolean(data.cancelAtPeriodEnd),
        currentPeriodEnd         : data.currentPeriodEnd || null,

        // NEW (debug + future RouteGuard guards)
        currentPeriodEndMs       : currentPeriodEndMs,
        stillInPaidTerm          : stillInPaidTerm,

        renewalDate              : renewalDateISO,
        lastBilledAmount         : data.lastBilledAmount || 0,
        hasValidPaymentMethod    : data.hasValidPaymentMethod || false,
        trialStartsAt            : toISO(data.trialStartsAt),
        trialEndsAt              : toISO(data.trialEndsAt),
        pendingStripeSchedule    : data.pendingStripeSchedule || null,
      };

      setCurrentSubscription((prevSub) => {
        if (areSubscriptionsMeaningfullyEqual(prevSub, nextSub)) {
          console.log(
            "[BillingContext] currentSubscription unchanged — skipping state update.",
            {
              subscriptionId: nextSub.id || null,
              priceId: nextSub.priceId || null,
              seats: nextSub.seats || null,
              status: nextSub.status || null,
            },
          );

          currentSubscriptionRef.current = prevSub || nextSub;
          return prevSub;
        }

        console.log(
          "[BillingContext] currentSubscription changed — applying state update.",
          {
            previous: prevSub || null,
            next: nextSub,
          },
        );

        console.log(
          "[BillingContext] pending immediate billing state:",
          {
            subscriptionId: nextSub.id || null,
            settledSeatCount: nextSub.seats || null,
            settledPriceId: nextSub.priceId || null,
            rawPendingStripeChange: rawPendingStripeChange || null,
            normalizedPendingStripeChange:
              normalizedPendingStripeChange || null,
            reconciledPendingStripeChange:
              pendingStripeChange || null,
            lastReconciledPendingInvoiceId:
              lastReconciledPendingInvoiceIdRef.current || null,
            lastReconciledPendingInvoiceStatus:
              lastReconciledPendingInvoiceStatusRef.current || "idle",
          },
        );

        currentSubscriptionRef.current = nextSub;
        return nextSub;
      });

setPendingBillingChange((prevPendingBillingChange) => {
  const nextPendingBillingChange = pendingStripeChange || null;

  if (
    arePendingBillingChangesMeaningfullyEqual(
      prevPendingBillingChange,
      nextPendingBillingChange,
    )
  ) {
    console.log(
      "[BillingContext] pendingBillingChange unchanged — skipping state update.",
      {
        pendingBillingChange:
          prevPendingBillingChange || nextPendingBillingChange || null,
      },
    );
    return prevPendingBillingChange;
  }

  console.log(
    "[BillingContext] Applying pending billing change state update:",
    {
      previousPendingBillingChange: prevPendingBillingChange || null,
      nextPendingBillingChange,
    },
  );

  /* ------------------------------------------------------------
   * ENTERPRISE STABILIZATION RULE
   *
   * Do NOT clear reconciliation memory merely because the legacy
   * pendingBillingChange export is transitioning to null.
   *
   * Why:
   * - BillingContext may already have classified the current invoice as
   *   stale / terminal.
   * - Firestore can still re-emit that same raw pending payload briefly
   *   while webhook + sync paths settle.
   * - If we clear lastReconciledPendingInvoice* here, we lose the memory
   *   that suppresses repeated stale-invoice re-entry.
   *
   * Reconciliation memory should instead be cleared only when:
   * - a materially NEW pending invoice id arrives, or
   * - a later provider-owned flow intentionally supersedes it.
   * ---------------------------------------------------------- */

  return nextPendingBillingChange;
});

      setPendingChangeState((prevPendingChangeState) => {
        const nextPendingBillingChange = pendingStripeChange || null;

        if (!nextPendingBillingChange) {
          console.log(
            "[BillingContext] Clearing normalized pendingChangeState " +
            "because no actionable pending change remains in the " +
            "settled org snapshot.",
          );

          return createEmptyPendingChangeState();
        }

        if (
          arePendingBillingChangesMeaningfullyEqual(
            prevPendingChangeState?.pendingChange || null,
            nextPendingBillingChange,
          )
        ) {
          console.log(
            "[BillingContext] pendingChangeState candidate unchanged — preserving existing normalized runtime state.",
            {
              previousPendingChangeState: prevPendingChangeState || null,
              nextPendingBillingChange,
            },
          );
          return prevPendingChangeState;
        }

        console.log(
          "[BillingContext] Applying normalized pendingChangeState candidate:",
          {
            previousPendingChangeState: prevPendingChangeState || null,
            nextPendingChangeStateCandidate,
          },
        );

        return nextPendingChangeStateCandidate;
      });

      setHasValidPaymentMethod((prevHasValidPaymentMethod) => {
        const nextHasValidPaymentMethod =
          data.hasValidPaymentMethod || false;

        if (prevHasValidPaymentMethod === nextHasValidPaymentMethod) {
          return prevHasValidPaymentMethod;
        }

        return nextHasValidPaymentMethod;
      });

      // NEW — first snapshot arrived → banner logic can run
      if (!billingLoadedRef.current) {
        billingLoadedRef.current = true;
        setBillingLoaded(true);
      }

      /* ──────────────────────────────────────────────────────────
       *  Couldn’t map → kick off ONE callable sync to fetch latest
       *  subscription from Stripe → will write priceId + planName.
       *  We gate it behind a flag so we don’t loop forever.
       * ─────────────────────────────────────────────────────────*/
      if (
        canonicalKey === "unknown" &&
        !billingSyncTriggeredRef.current
      ) {
        console.warn(
          "[BillingContext] Plan unknown – triggering one-time sync…"
        );
        billingSyncTriggeredRef.current = true;          // flag
        syncStripeSubscriptionDetails()
          .catch(err => console.error(
            "[BillingContext] one-time sync failed", err
          ));
      }
    });

    return unsubscribe;
  }, [
    isOrgReady,
    organization?.id,
    syncStripeSubscriptionDetails,
    normalizePendingBillingChange,
    suppressPendingBillingChangeFromReconciledMemory,
    arePendingBillingChangesMeaningfullyEqual,
  ]);

 /* ────────────────────────────────────────────────────────────────
  * Cold-start reconcile:
  *  • After first snapshot (billingLoaded=true) and org is ready,
  *    if the subscription is not active/trialing, pull fresh data
  *    from Stripe once and wait briefly for the onSnapshot to reflect
  *    the change before any guards decide access.
  * ─────────────────────────────────────────────────────────────── */
 useEffect(() => {
   if (!isOrgReady || !billingLoaded || !organization?.stripeCustomerId) {
     return;
   }

  if (reconciling) return;  // already reconciling; avoid overlap

   const status = currentSubscription?.status;
   const hasPlan =
     !!currentSubscription?.priceId || !!currentSubscription?.planName;

   // Already good → nothing to do
   if ((status === "active" || status === "trialing") && hasPlan) return;

   let cancel = false;
   (async () => {
     setReconciling(true);
     try {
       // Force a Stripe → Firestore sync
       await syncStripeSubscriptionDetails();

       // Give the listener a short window to deliver fresh fields
       for (let i = 0; i < 6; i++) { // ~3s total (6 × 500ms)
         if (cancel) return;
         await new Promise(r => setTimeout(r, 500));
         const latest = currentSubscriptionRef.current;
         const s = latest?.status;
         const p = !!latest?.priceId || !!latest?.planName;

         if ((s === "active" || s === "trialing") && p) break;
         // One mid-loop nudge to sync again if still stale
         if (i === 2) {
           try { await syncStripeSubscriptionDetails(); } catch {}
         }
       }
     } finally {
       if (!cancel) setReconciling(false);
     }
   })();

   return () => { cancel = true; };
 }, [
   isOrgReady,
   billingLoaded,
   organization?.stripeCustomerId,
   currentSubscription?.status,
   currentSubscription?.priceId,
   currentSubscription?.planName,
   syncStripeSubscriptionDetails,
 ]);

  /* ────────────────────────────────────────────────────────────────
   * Legacy compatibility sync
   *
   * Why:
   * - Existing consumers still read pendingBillingChangeStatus and
   *   pendingBillingChangeInvoiceDetails.
   * - During the provider-centered refactor, these fields are derived from
   *   the normalized pendingChangeState source of truth.
   * ---------------------------------------------------------------- */
  useEffect(() => {
    const nextLegacyStatus =
      mapPendingChangeStateKindToLegacyStatus(pendingChangeState.kind);

    if (pendingBillingChangeStatus !== nextLegacyStatus) {
      setPendingBillingChangeStatus(nextLegacyStatus);
    }

    const nextLegacyInvoiceDetails =
      pendingChangeState.invoiceDetails || null;

    const invoiceDetailsChanged =
      JSON.stringify(pendingBillingChangeInvoiceDetails || null) !==
      JSON.stringify(nextLegacyInvoiceDetails);

    if (invoiceDetailsChanged) {
      setPendingBillingChangeInvoiceDetails(nextLegacyInvoiceDetails);
    }
  }, [
    pendingChangeState,
    pendingBillingChangeStatus,
    pendingBillingChangeInvoiceDetails,
  ]);

  /* ─────────────────────────  invoices  ─────────────────────────────── */
  const fetchInvoices = useCallback(async () => {
    if (!organization?.stripeCustomerId) return;

    setLoading(true, "BillingsContext");
    try {
      const callable = httpsCallable(functions, "fetchStripeInvoices");
      const { data } = await callable({ customerId: organization.stripeCustomerId });

      const normal = (data.invoices || []).map(inv => ({
        id    : inv.id,
        number: inv.number ?? "—",
        amount: (inv.total || 0) / 100,
        date  : inv.created ? new Date(inv.created * 1000).toLocaleDateString() : "—",
        url   : inv.hosted_invoice_url || "#",
      }));
      setInvoices(normal);
    } catch (err) {
      console.error("[BillingContext] fetchInvoices", err);
    } finally {
      setLoading(false, "BillingsContext");
    }
  }, [organization?.stripeCustomerId, setLoading]);

  /* ─────────────────────────  plans  ───────────────────────────── */
  const hydratePlansFromCatalog = useCallback(() => {
    /* Turn the planConfigs object → array the UI already expects */
    const arr = Object.entries(planConfigs)
      /* ------------------------------------------------------------
       * Launch visibility:
       *  • Base plans: cfg.kind === "base"
       *  • Add-ons   : cfg.kind === "addon"  (hidden until Stripe ready)
       *
       * This is UI-only; it does NOT change Stripe syncing/webhooks.
       * ---------------------------------------------------------- */
      .filter(([, cfg]) => cfg?.kind === "base")
      .flatMap(([key, cfg]) =>
        Object.entries(cfg.pricing).map(([interval, tier]) => ({
          key,                        // e.g. 'standard'
          name      : key.charAt(0).toUpperCase() + key.slice(1), // 'Standard'
          interval,  // 'monthly' | 'annual'
          priceId   : tier.priceId,
          flatFee   : tier.flatFee,
          perSeat   : tier.perSeat,
          features  : cfg.features,
          subheader : cfg.subheader,
        }))
      );

    setPlans(arr);
//    console.log("[BillingContext] Plans hydrated from catalog:", arr);
  }, []);


  /* ───────────────────  Stripe Customer Portal redirect  ────────────── */
  const redirectToCustomerPortal = useCallback(async () => {
    const customerId = organization?.stripeCustomerId || currentSubscription?.customerId;
    if (!customerId) return;

    setLoading(true, "BillingsContext");
    try {
      const callable = httpsCallable(functions, "createCustomerPortalSession");
      const { data }  = await callable({
        customerId,
        returnUrl: window.location.origin + "/settings/billing-settings",
      });
      window.location.href = data.url;
    } catch (err) {
      console.error("[BillingContext] redirectToCustomerPortal", err);
    } finally {
      setLoading(false, "BillingsContext");
    }
  }, [organization?.stripeCustomerId, currentSubscription?.customerId, setLoading]);

 /* ───────────────────  Billing-portal UPDATE flow  ─────────────────── */
 const openPortalUpdate = useCallback(async () => {
   const cid = organization?.stripeCustomerId;
   const sid = currentSubscription?.id;
   if (!cid || !sid) {
     console.warn("[BillingContext] openPortalUpdate - missing ids:", {
       cid, sid,
     });
     return;
   }

   setLoading(true, "BillingsContext");
   try {
     const callable =
       httpsCallable(functions, "createPortalUpdateSession");
     const { data } = await callable({
       customerId    : cid,
       subscriptionId: sid,
       returnUrl     : window.location.origin +
         "/settings/billing-settings",
     });
     window.location.href = data.url;   // ← hand off to Stripe portal
   } catch (err) {
     console.error("[BillingContext] openPortalUpdate", err);
   } finally {
     setLoading(false, "BillingsContext");
   }
 }, [
   organization?.stripeCustomerId,
   currentSubscription?.id,
   setLoading,
 ]);


  /* createPaymentIntent — keeps the fine-grained flow for one-off upgrades */
  const createPaymentIntent = useCallback(async ({ priceId, seatCount }) => {
    if (!organization?.stripeCustomerId) return null;

    setLoading(true, "BillingsContext");
    try {
      const callable = httpsCallable(functions, "createPaymentIntent");
      const { data }  = await callable({
        customerId: organization.stripeCustomerId,
        priceId,
        seatCount,
      });

      console.log(
        "[BillingContext] createPaymentIntent ← response:",
        data,
      );

      return {
        clientSecret: data.clientSecret || null,
        paymentIntentId: data.paymentIntentId || null,
      };
    } catch (err) {
      console.error("[BillingContext] createPaymentIntent", err);
      return null;
    } finally {
      setLoading(false, "BillingsContext");
    }
  }, [organization?.stripeCustomerId, setLoading]);

/* ───────────────────────────────────────────────────────────────
 * Interval-switch flow → call switchIntervalAndPay
 *  • Edits the sub in-place
 *  • CF returns:
 *      { needsPayment:true ,  url }  → redirect to Checkout
 *      { needsPayment:false }        → nothing to pay, just refresh
 * ─────────────────────────────────────────────────────────────*/
const switchIntervalAndPay = useCallback(
  async ({ newPriceId, seatCount }) => {
    if (
      !organization?.stripeCustomerId ||
      !currentSubscription?.id ||
      !currentSubscription?.stripeSubscriptionItemId
    ) {
      console.warn("[BillingContext] switchIntervalAndPay – missing ids");
      return null;
    }

    /* ─────────────────────────────────────────────────────────────
     * SALES-LED GUARD (NEW)
     * If the user is attempting to switch to a contact-sales plan,
     * do NOT call CF. Redirect immediately.
     * ─────────────────────────────────────────────────────────── */
    if (newPriceId && isContactSalesPriceId(newPriceId)) {
      console.warn(
        "[BillingContext] switchIntervalAndPay local-block → CONTACT_SALES",
        { newPriceId },
      );
      redirectToContactSales();
      return null;
    }

    setLoading(true, "BillingsContext");
    try {
      const callable = httpsCallable(functions, "switchIntervalAndPay");

      const { data } = await callable({
        customerId        : organization.stripeCustomerId,
        subscriptionId    : currentSubscription.id,
        subscriptionItemId: currentSubscription.stripeSubscriptionItemId,
        newPriceId,
        seatCount,
        successUrl: CHECKOUT_RESULT_URL,
        cancelUrl : CHECKOUT_RESULT_URL,
      });

      // Option B: scheduled downgrade (no redirect, no invoice today)
      if (data?.scheduled && data?.effectiveAt) {
        console.log(
          "[BillingContext] scheduled downgrade:",
          data,
        );
        // Optional: refresh local org snapshot so UI can show schedule mirror
        try { await syncStripeSubscriptionDetails(); } catch {}
        return data;
      }

      /* ----------------------------------------------------------
       * Enterprise ownership boundary
       *
       * IMPORTANT:
       * - BillingContext must NOT own browser navigation for the new
       *   SubscriptionPage2 / ReviewChangesDrawer2 flow.
       * - It should only return authoritative backend results so the
       *   UI layer can decide whether to:
       *     • open Stripe in a new tab
       *     • reuse the current tab
       *     • close the drawer
       *     • navigate to checkout-result
       *
       * Why:
       * - ReviewChangesDrawer2 now owns the final handoff UX.
       * - If BillingContext also redirects, both layers compete and
       *   produce blank tabs, double-navigation, and stale UI.
       * -------------------------------------------------------- */
      if (data?.needsPayment && data?.url) {
        return data;
      }

      if (data?.needsPayment === false) {
        await syncStripeSubscriptionDetails();
        return data;
      }

      // Fallback: return whatever we got
      return data || null;

    } catch (err) {
      if (isContactSalesHttpsError(err)) {
        console.warn(
          "[BillingContext] switchIntervalAndPay blocked → CONTACT_SALES",
          err,
        );
        redirectToContactSales();
        return null;
      }

      console.error("[BillingContext] switchIntervalAndPay ⤫", err);
      return null;
    } finally {
      setLoading(false, "BillingsContext");
    }
  },
  [
    organization?.stripeCustomerId,
    currentSubscription,
    syncStripeSubscriptionDetails,
    setLoading,
    redirectToContactSales,
    isContactSalesPriceId,
  ],
);


/* ---------------------------------------------------------------
 * Transitional Checkout-session helper
 *
 * IMPORTANT
 * - This is NOT the primary immediate-mutation path anymore.
 * - The enterprise-grade billing flow now expects:
 *     • switchIntervalAndPay() for most immediate seat/plan edits
 * - This helper should only remain available for true Checkout-driven
 *   paths that the backend still intentionally supports.
 *
 * Maintenance rule:
 * - Do NOT expand this back into a general seat/plan mutation helper.
 * ------------------------------------------------------------- */
// returns the backend response so the caller can decide how to use it
const createCheckoutSession = useCallback(
  async ({
    priceId = null,
    seatCount,
    subscriptionId,        // may be undefined – caller can supply
    subscriptionItemId,    // may be undefined – caller can supply
    previousSeatCount,     // may be undefined – caller can supply
  }) => {
    /* -----------------------------------------------------------------
     * Use the values from the caller when they’re provided;
     * otherwise fall back to currentSubscription (old behaviour).
     * ----------------------------------------------------------------*/
    const subId = subscriptionId ?? currentSubscription?.id;
    const itemId =
      subscriptionItemId ?? currentSubscription?.stripeSubscriptionItemId;

    /* ------------------------------------------------------------
     * For true Checkout-driven plan / interval switches, the backend
     * now requires the PREVIOUS settled seat count so it can write
     * correct migration metadata (prev_quantity) into Checkout.
     *
     * Enterprise-grade alignment:
     * - Prefer an explicitly supplied previousSeatCount when the caller
     *   is operating on a specific settled subscription context.
     * - Otherwise fall back to the currently hydrated subscription snapshot.
     *
     * Seat-only calls do not need this because they should never hit
     * createCheckoutSession anymore.
     * ---------------------------------------------------------- */
    const resolvedPreviousSeatCount =
      Number.isFinite(Number(previousSeatCount)) &&
      Number(previousSeatCount) >= 1 ?
        Number(previousSeatCount) :
        (
          Number.isFinite(Number(currentSubscription?.seats)) &&
          Number(currentSubscription.seats) >= 1 ?
            Number(currentSubscription.seats) :
            null
        );

    if (
      !organization?.stripeCustomerId ||
      !subId ||
      !itemId
    ) {
      console.warn("[BillingContext] createCheckoutSession – missing IDs:", {
        customerId: organization?.stripeCustomerId,
        subscriptionId: subId,
        subscriptionItemId: itemId,
      });
      return null;
    }

    /* ------------------------------------------------------------
     * For true Checkout-driven plan / interval switches, enforce that
     * we have the settled previous seat count required by the backend.
     *
     * Why:
     * - stripeFunctions.js now validates previousSeatCount for non-seat-only
     *   Checkout flows.
     * - Failing fast here gives cleaner diagnostics and keeps the frontend
     *   contract aligned with the backend mutation contract.
     * ---------------------------------------------------------- */
    if (priceId && resolvedPreviousSeatCount === null) {
      console.warn(
        "[BillingContext] createCheckoutSession – missing previousSeatCount " +
        "for Checkout-driven plan/interval switch.",
        {
          customerId: organization?.stripeCustomerId || null,
          subscriptionId: subId,
          subscriptionItemId: itemId,
          priceId,
          previousSeatCount,
          currentSubscriptionSeats: currentSubscription?.seats ?? null,
        },
      );
      return null;
    }

    /* ─────────────────────────────────────────────────────────────
     * SALES-LED GUARD (NEW)
     * If the user selected a contact-sales plan, do NOT call CF.
     * Redirect immediately to the marketing contact-sales page.
     * ─────────────────────────────────────────────────────────── */
    if (priceId && isContactSalesPriceId(priceId)) {
      console.warn(
        "[BillingContext] createCheckoutSession local-block → CONTACT_SALES",
        { priceId },
      );
      redirectToContactSales();
      return null;
    }

    setLoading(true, "BillingsContext");
    try {
      const callable = httpsCallable(functions, "createCheckoutSession");

      const payload = {
        customerId: organization.stripeCustomerId,
        subscriptionId: subId,
        subscriptionItemId: itemId,
        seatCount,
        ...(priceId && { priceId }),
        ...(priceId && resolvedPreviousSeatCount !== null ?
          { previousSeatCount: resolvedPreviousSeatCount } :
          {}),
        successUrl: CHECKOUT_RESULT_URL,
        cancelUrl: CHECKOUT_RESULT_URL,
      };

      console.log(
        "[BillingContext] createCheckoutSession → payload:",
        payload,
      );

      const { data } = await callable(payload);

      console.log(
        "[BillingContext] createCheckoutSession ← response:",
        data,
      );

      return data || null;
    } catch (err) {
      const code = err?.code || null;
      const message = (err?.message || "").toString();

      /* ─────────────────────────────────────────────────────────────
       * SALES-LED FALLBACK (NEW)
       * If CF blocks with CONTACT_SALES, redirect immediately.
       * This MUST run before the generic failed-precondition handler,
       * because CONTACT_SALES is also thrown as failed-precondition.
       * ─────────────────────────────────────────────────────────── */
      if (isContactSalesHttpsError(err)) {
        console.warn(
          "[BillingContext] createCheckoutSession blocked → CONTACT_SALES",
          err,
        );
        redirectToContactSales();
        return null;
      }

      /* ----------------------------------------------------------
       * Canonical backend delegation
       *
       * When createCheckoutSession throws failed-precondition, the
       * backend is explicitly telling us this request belongs in the
       * canonical immediate-mutation lane instead:
       *   • seat-only change
       *   • same-interval plan change
       *
       * That lane is switchIntervalAndPay().
       *
       * IMPORTANT:
       * - Keep this fallback narrow.
       * - Do NOT add new mutation behavior here unless the backend
       *   contract changes intentionally.
       * - Preserve the delegated backend response so callers receive
       *   the same enterprise-grade contract shape they would have
       *   received if they had called switchIntervalAndPay directly.
       * - Only delegate when the backend EXPLICITLY says this request
       *   belongs in switchIntervalAndPay().
       * -------------------------------------------------------- */
      if (code === "functions/failed-precondition") {
        const shouldDelegateToImmediateLane =
          message.includes(
            "Use switchIntervalAndPay for seat-only or same-interval edits.",
          );

        if (!shouldDelegateToImmediateLane) {
          console.warn(
            "[BillingContext] createCheckoutSession received " +
            "failed-precondition that should NOT be delegated:",
            {
              code,
              message,
              err,
            },
          );
          return null;
        }

        // If there is NO priceId, this was a "seat-only/same-interval" request.
        // Do NOT auto-run switchIntervalAndPay from here unless the caller
        // intentionally sent a plan context for delegation.
        if (!priceId) {
          console.warn(
            "[BillingContext] createCheckoutSession blocked: missing priceId. " +
            "Backend indicated switchIntervalAndPay, but this caller did not " +
            "provide a plan priceId to delegate with. Navigate user to " +
            "/subscription to choose a plan instead.",
          );
          return null;
        }

        // If priceId exists and the backend EXPLICITLY says this belongs in
        // switchIntervalAndPay, delegate and RETURN the backend response.
        const delegatedResult = await switchIntervalAndPay({
          newPriceId: priceId,
          seatCount,
        });

        console.log(
          "[BillingContext] createCheckoutSession delegated to " +
          "switchIntervalAndPay:",
          delegatedResult,
        );

        return delegatedResult || null;
      }

      console.error("[BillingContext] createCheckoutSession", err);
      return null;
    } finally {
      setLoading(false, "BillingsContext");
    }
  },
  [
    organization?.stripeCustomerId,
    currentSubscription,
    setLoading,
    switchIntervalAndPay,
    redirectToContactSales,
    isContactSalesPriceId,
  ],
);
/* ---------------------------------------------------
 * Unified seat / plan change
 *
 * ENTERPRISE RULE
 *  • All seat-count / plan mutations must flow through
 *    switchIntervalAndPay().
 *  • Do NOT keep a separate Billing Portal mutation lane for
 *    subscription changes, because that creates multiple write
 *    paths and increases billing-state drift risk.
 *
 * Why:
 *  • switchIntervalAndPay already handles:
 *      - seat-only edits
 *      - same-interval plan changes
 *      - interval switches
 *      - downgrade scheduling
 *      - restart flows
 *      - manual payment / hosted invoice flows
 *  • This keeps BillingContext aligned with the backend's
 *    single mutation path.
 * --------------------------------------------------*/
const updateSeats = useCallback(
  async (seatCount, newPriceId = null) => {
    if (
      !currentSubscription?.id ||
      !currentSubscription?.stripeSubscriptionItemId
    ) {
      console.warn(
        "[BillingContext] updateSeats – missing subscription ids",
      );
      return;
    }

    /* ------------------------------------------------------------
     * 0. Short-circuit: nothing actually changed
     * ------------------------------------------------------------*/
    const nothingChanged =
      seatCount === currentSubscription.seats &&
      (newPriceId === null || newPriceId === currentSubscription.priceId);

    if (nothingChanged) {
//      console.log("[BillingContext] updateSeats – no-op, aborting.");
      return;
    }

    /* ------------------------------------------------------------
     * 1. Single enterprise mutation path
     *
     * IMPORTANT:
     * - Do not branch into updateSubscriptionPlanAndOpenPortal here.
     * - Let switchIntervalAndPay decide the correct downstream path:
     *     • immediate payment
     *     • hosted invoice
     *     • scheduled downgrade
     *     • restart
     * -----------------------------------------------------------*/
//    console.log("[BillingContext] updateSeats → switchIntervalAndPay", {
////      seatCount,
////      newPriceId: newPriceId ?? null,
////      subscriptionId: currentSubscription.id,
////      subscriptionItemId: currentSubscription.stripeSubscriptionItemId,
////    });

    return await switchIntervalAndPay({
      newPriceId: newPriceId ?? null,
      seatCount,
    });
  },
  [
    currentSubscription,
    switchIntervalAndPay,
  ],
);




/* Fetch proration preview ------------------------------------------ */
const fetchProrationDetails = useCallback(
  async ({
    subscriptionId,
    stripeSubscriptionItemId,
    newPriceId = null,
    seatCount,
  }) => {
//  console.log('[BillingContext] UI about to preview →', {
//    seatCountFrontEnd: seatCount,
//   priceIdFrontEnd:
//     newPriceId ?? currentSubscription?.priceId ?? '(n/a)',
//  });


    if (!subscriptionId || !stripeSubscriptionItemId) return null;

    /* ─────────────────────────────────────────────────────────────
     * SALES-LED GUARD (NEW)
     * Never preview proration for contact-sales plans.
     * ─────────────────────────────────────────────────────────── */
    if (newPriceId && isContactSalesPriceId(newPriceId)) {
      console.warn(
        "[BillingContext] fetchProrationDetails local-block → CONTACT_SALES",
        { newPriceId },
      );
      return null;
    }

setLoading(true, "BillingsContext");
try {
  const callable = httpsCallable(functions, "fetchProrationDetails");

  /* ── NEW: ensure we have a customerId (snapshot or org fallback) ── */
  const resolvedCustomerId =
    currentSubscription?.customerId || organization?.stripeCustomerId || null;

  if (!resolvedCustomerId) {
    console.warn(
      "[BillingContext] fetchProrationDetails – missing customerId; " +
      "skipping preview until IDs are ready.",
    );
    return null;
  }

  /* ——— 1. build payload *then* log it ——— */
  const payload = {
    subscriptionId,
    stripeSubscriptionItemId,
    ...(newPriceId && { newPriceId }), // only if changing plans
    newSeatCount: seatCount,
    customerId: resolvedCustomerId, // ← fallback resolver (critical)
  };
//      console.log(
//        "[BillingContext] fetchProrationDetails → payload",
//        JSON.stringify(payload, null, 2),
//      );

  /* ——— 2. invoke CF ——— */
  const { data } = await callable(payload);
  return data;
} catch (err) {
  console.error("[BillingContext] fetchProrationDetails ⤫", err);
  return null;
} finally {
  setLoading(false, "BillingsContext");
}

  },
  [setLoading, currentSubscription, organization?.stripeCustomerId,
    isContactSalesPriceId],
);

/* Preview billing change (preview-first architecture) ------------- */
const previewBillingChange = useCallback(
  async ({
    subscriptionId,
    subscriptionItemId,
    newPriceId = null,
    seatCount,
  }) => {
    if (!subscriptionId || !subscriptionItemId) {
      console.warn(
        "[BillingContext] previewBillingChange skipped: missing ids.",
        {
          subscriptionId: subscriptionId || null,
          subscriptionItemId: subscriptionItemId || null,
        },
      );
      return null;
    }

    if (
      typeof seatCount !== "number" ||
      !Number.isFinite(seatCount) ||
      seatCount < 1
    ) {
      console.warn(
        "[BillingContext] previewBillingChange skipped: invalid seatCount.",
        {
          seatCount,
        },
      );
      return null;
    }

    if (newPriceId && isContactSalesPriceId(newPriceId)) {
      console.warn(
        "[BillingContext] previewBillingChange local-block → CONTACT_SALES",
        { newPriceId },
      );
      return null;
    }

    const resolvedCustomerId =
      currentSubscription?.customerId ||
      organization?.stripeCustomerId ||
      null;

    if (!resolvedCustomerId) {
      console.warn(
        "[BillingContext] previewBillingChange skipped: missing customerId.",
      );
      return null;
    }

    setLoading(true, "BillingsContext");
    try {
      const callable = httpsCallable(functions, "previewBillingChange");

      const payload = {
        customerId: resolvedCustomerId,
        subscriptionId,
        subscriptionItemId,
        seatCount,
        ...(newPriceId ? { newPriceId } : {}),
      };

      console.log(
        "[BillingContext] previewBillingChange → payload:",
        payload,
      );

      const { data } = await callable(payload);

      console.log(
        "[BillingContext] previewBillingChange ← response:",
        data,
      );

      return data || null;
    } catch (err) {
      if (isContactSalesHttpsError(err)) {
        console.warn(
          "[BillingContext] previewBillingChange blocked → CONTACT_SALES",
          err,
        );
        redirectToContactSales();
        return null;
      }

      console.error("[BillingContext] previewBillingChange ⤫", err);
      return null;
    } finally {
      setLoading(false, "BillingsContext");
    }
  },
  [
    setLoading,
    currentSubscription,
    organization?.stripeCustomerId,
    isContactSalesPriceId,
    redirectToContactSales,
  ],
);

/* Get invoice details -------------------------------------------- */
const getInvoiceDetails = useCallback(async ({ invoiceId }) => {
  if (!invoiceId) {
    console.warn(
      "[BillingContext] getInvoiceDetails skipped: missing invoiceId.",
    );
    return null;
  }

  const now = Date.now();
  const cached = invoiceDetailsCacheRef.current.get(invoiceId) || null;

  if (
    cached &&
    now - cached.cachedAt < INVOICE_DETAILS_CACHE_TTL_MS
  ) {
    console.log(
      "[BillingContext] getInvoiceDetails cache hit:",
      {
        invoiceId,
        cachedAt: cached.cachedAt,
        ageMs: now - cached.cachedAt,
      },
    );

    return cached.data || null;
  }

  const inFlight = invoiceDetailsInFlightRef.current.get(invoiceId) || null;
  if (inFlight) {
    console.log(
      "[BillingContext] getInvoiceDetails reusing in-flight request:",
      { invoiceId },
    );
    return inFlight;
  }

  setLoading(true, "BillingsContext");

  const requestPromise = (async () => {
    try {
      const callable = httpsCallable(functions, "getInvoiceDetails");

      const payload = { invoiceId };

      console.log(
        "[BillingContext] getInvoiceDetails → payload:",
        payload,
      );

      const { data } = await callable(payload);

      console.log(
        "[BillingContext] getInvoiceDetails ← response:",
        data,
      );

      invoiceDetailsCacheRef.current.set(invoiceId, {
        data: data || null,
        cachedAt: Date.now(),
      });

      return data || null;
    } catch (err) {
      console.error("[BillingContext] getInvoiceDetails ⤫", err);
      return null;
    } finally {
      invoiceDetailsInFlightRef.current.delete(invoiceId);
      setLoading(false, "BillingsContext");
    }
  })();

  invoiceDetailsInFlightRef.current.set(invoiceId, requestPromise);

  return requestPromise;
}, [setLoading]);

/* Cancel pending billing change ------------------------------- */
const cancelPendingBillingChange = useCallback(async () => {
  const resolvedCustomerId =
    currentSubscription?.customerId ||
    organization?.stripeCustomerId ||
    null;

  if (!resolvedCustomerId) {
    console.warn(
      "[BillingContext] cancelPendingBillingChange skipped: " +
      "missing customerId.",
    );
    return null;
  }

  setLoading(true, "BillingsContext");
  try {
    const callable = httpsCallable(
      functions,
      "cancelPendingBillingChange",
    );

    const payload = {
      customerId: resolvedCustomerId,
    };

    console.log(
      "[BillingContext] cancelPendingBillingChange → payload:",
      payload,
    );

    const { data } = await callable(payload);

    console.log(
      "[BillingContext] cancelPendingBillingChange ← response:",
      data,
    );

    if (data?.success) {
      try {
        await syncStripeSubscriptionDetails({
          stripeCustomerIdOverride: resolvedCustomerId,
        });
      } catch (syncErr) {
        console.warn(
          "[BillingContext] cancelPendingBillingChange " +
          "post-cancel sync failed:",
          syncErr,
        );
      }
    }

    return data || null;
  } catch (err) {
    console.error("[BillingContext] cancelPendingBillingChange ⤫", err);
    return null;
  } finally {
    setLoading(false, "BillingsContext");
  }
}, [
  currentSubscription?.customerId,
  organization?.stripeCustomerId,
  functions,
  setLoading,
  syncStripeSubscriptionDetails,
]);

/* Cancel scheduled downgrade ------------------------------- */
const cancelScheduledDowngrade = useCallback(async () => {
  const resolvedCustomerId =
    currentSubscription?.customerId ||
    organization?.stripeCustomerId ||
    null;

  if (!resolvedCustomerId) {
    console.warn(
      "[BillingContext] cancelScheduledDowngrade skipped: missing customerId.",
    );
    return null;
  }

  setLoading(true, "BillingsContext");
  try {
    const callable = httpsCallable(
      functions,
      "cancelScheduledDowngrade",
    );

    const payload = {
      customerId: resolvedCustomerId,
    };

    console.log(
      "[BillingContext] cancelScheduledDowngrade → payload:",
      payload,
    );

    const { data } = await callable(payload);

    console.log(
      "[BillingContext] cancelScheduledDowngrade ← response:",
      data,
    );

    if (data?.success) {
      try {
        await syncStripeSubscriptionDetails({
          stripeCustomerIdOverride: resolvedCustomerId,
        });
      } catch (syncErr) {
        console.warn(
          "[BillingContext] cancelScheduledDowngrade post-cancel sync failed:",
          syncErr,
        );
      }
    }

    return data || null;
  } catch (err) {
    console.error("[BillingContext] cancelScheduledDowngrade ⤫", err);
    return null;
  } finally {
    setLoading(false, "BillingsContext");
  }
}, [
  currentSubscription?.customerId,
  organization?.stripeCustomerId,
  functions,
  setLoading,
  syncStripeSubscriptionDetails,
]);


/* Cancel subscription --------------------------------------------- */
const cancelSubscription = useCallback(async (subscriptionId) => {
  if (!subscriptionId) return;

    setLoading(true, "BillingsContext");
    try {
      const callable = httpsCallable(functions, "cancelStripeSubscription");
      await callable({ subscriptionId });
    } catch (err) {
      console.error("[BillingContext] cancelSubscription", err);
    } finally {
      setLoading(false, "BillingsContext");
    }
  }, [setLoading]);

  /* ─────────────────────────  bootstrap  ──────────────────────────── */
  useEffect(() => {
    if (!isOrgReady) return;

    const unsub = subscribeToOrgDoc();

    hydratePlansFromCatalog();  // one-shot; now uses catalog

    return () => unsub && unsub();
  }, [
    isOrgReady,
    subscribeToOrgDoc,
    hydratePlansFromCatalog,
  ]);

  /* ────────────────────────────────────────────────────────────────
   * Second-stage pending-change reconciliation
   *
   * Gold-standard ownership rule:
   * - BillingContext owns the immediate pending-change runtime state machine.
   * - Firestore snapshot normalization only determines whether an actionable
   *   pending change candidate exists.
   * - This effect is the ONLY place that upgrades a candidate pending change
   *   into:
   *     • resume_payment
   *     • transitional
   *     • stale
   *     • error
   *
   * IMPORTANT:
   * - Downstream UI should eventually consume pendingChangeState directly.
   * - Legacy fields remain temporarily for compatibility.
   * ---------------------------------------------------------------- */
  useEffect(() => {
    let cancelled = false;

    const pendingChangeForReconciliation =
      pendingChangeState?.pendingChange || null;

    const currentPendingInvoiceId =
      pendingChangeForReconciliation?.invoiceId || null;

    const pendingBillingChangeReconciliationKey =
      pendingChangeForReconciliation ?
        JSON.stringify({
          invoiceId: pendingChangeForReconciliation?.invoiceId || null,
          subscriptionId:
            pendingChangeForReconciliation?.subscriptionId || null,
          nextSeatCount:
            Number.isFinite(
              Number(pendingChangeForReconciliation?.nextSeatCount),
            ) ?
              Number(pendingChangeForReconciliation.nextSeatCount) :
              null,
          nextPriceId:
            pendingChangeForReconciliation?.nextPriceId || null,
          requestedAt:
            pendingChangeForReconciliation?.requestedAt || null,
        }) :
        null;

    if (!currentPendingInvoiceId) {
      setPendingChangeState((prevPendingChangeState) => {
        if (prevPendingChangeState?.kind === "none") {
          return prevPendingChangeState;
        }

        console.log(
          "[BillingContext] Resetting normalized pendingChangeState " +
          "because there is no pending invoice to reconcile.",
          {
            previousPendingChangeState: prevPendingChangeState || null,
          },
        );

        return createEmptyPendingChangeState();
      });

      return undefined;
    }

    const requestId = pendingChangeReconSeqRef.current + 1;
    pendingChangeReconSeqRef.current = requestId;
    activePendingChangeReconRef.current = requestId;

    const isStale = () =>
      cancelled || activePendingChangeReconRef.current !== requestId;

    if (
      currentPendingInvoiceId &&
      lastReconciledPendingInvoiceIdRef.current &&
      lastReconciledPendingInvoiceIdRef.current !== currentPendingInvoiceId
    ) {
      console.log(
        "[BillingContext] New pending invoice detected — clearing prior reconciliation memory:",
        {
          previousInvoiceId: lastReconciledPendingInvoiceIdRef.current,
          nextInvoiceId: currentPendingInvoiceId,
          previousStatus: lastReconciledPendingInvoiceStatusRef.current,
          previousReconciliationKey:
            lastPendingReconciliationKeyRef.current,
        },
      );

      lastReconciledPendingInvoiceIdRef.current = null;
      lastReconciledPendingInvoiceStatusRef.current = "idle";
      lastPendingReconciliationKeyRef.current = null;
    }

    /* ------------------------------------------------------------
     * Short-circuit repeated reconciliation for the same invoice/key
     *
     * Why:
     * - Firestore may re-emit the same logical pending change several times
     *   while nothing materially changed.
     * - The provider should reconcile once per meaningful pending change
     *   identity, not once per object identity or render pass.
     * ---------------------------------------------------------- */
    if (
      currentPendingInvoiceId &&
      lastReconciledPendingInvoiceIdRef.current === currentPendingInvoiceId &&
      lastPendingReconciliationKeyRef.current ===
        pendingBillingChangeReconciliationKey &&
      (
        lastReconciledPendingInvoiceStatusRef.current === "actionable" ||
        lastReconciledPendingInvoiceStatusRef.current === "transitional" ||
        lastReconciledPendingInvoiceStatusRef.current === "stale"
      )
    ) {
      console.log(
        "[BillingContext] Skipping duplicate pending-change reconciliation for already-reconciled invoice/key:",
        {
          requestId,
          pendingChangeForReconciliation,
          pendingBillingChangeReconciliationKey,
          lastPendingReconciliationKey:
            lastPendingReconciliationKeyRef.current,
          lastReconciledPendingInvoiceId:
            lastReconciledPendingInvoiceIdRef.current,
          lastReconciledPendingInvoiceStatus:
            lastReconciledPendingInvoiceStatusRef.current,
          pendingChangeState,
        },
      );
      return undefined;
    }

    const reconcilePendingInvoice = async () => {
      setPendingChangeState((prevPendingChangeState) => {
        if (
          !prevPendingChangeState?.pendingChange ||
          prevPendingChangeState.pendingChange.invoiceId !==
            currentPendingInvoiceId
        ) {
          return prevPendingChangeState;
        }

        if (prevPendingChangeState.kind === "checking") {
          return prevPendingChangeState;
        }

        return {
          ...prevPendingChangeState,
          kind: "checking",
          invoiceClassification: "checking",
          canResumePayment: false,
        };
      });

      try {
        const invoiceDetails = await getInvoiceDetails({
          invoiceId: currentPendingInvoiceId,
        });

        if (isStale()) {
          console.log(
            "[BillingContext] Ignoring stale pending-change invoice reconciliation:",
            {
              requestId,
              pendingChangeForReconciliation,
              invoiceId: currentPendingInvoiceId,
            },
          );
          return;
        }

        const classification =
          classifyPendingInvoiceDetails(invoiceDetails);

        const nextResolvedPendingChangeState =
          buildResolvedPendingChangeState({
            pendingChange: pendingChangeForReconciliation,
            invoiceDetails,
            classification,
          });

        console.log(
          "[BillingContext] Pending-change invoice reconciliation result:",
          {
            requestId,
            pendingChangeForReconciliation,
            invoiceDetails,
            classification,
            nextResolvedPendingChangeState,
          },
        );

        lastReconciledPendingInvoiceIdRef.current =
          currentPendingInvoiceId;
        lastReconciledPendingInvoiceStatusRef.current =
          classification === "actionable" ?
            "actionable" :
            classification === "transitional" ?
              "transitional" :
              "stale";
        lastPendingReconciliationKeyRef.current =
          pendingBillingChangeReconciliationKey;

        setPendingChangeState((prevPendingChangeState) => {
          if (
            !prevPendingChangeState?.pendingChange ||
            prevPendingChangeState.pendingChange.invoiceId !==
              currentPendingInvoiceId
          ) {
            return prevPendingChangeState;
          }

          return nextResolvedPendingChangeState;
        });

        if (
          classification === "terminal" ||
          classification === "unknown"
        ) {
          setPendingBillingChange((prevPendingBillingChange) => {
            if (
              !prevPendingBillingChange ||
              prevPendingBillingChange.invoiceId !== currentPendingInvoiceId
            ) {
              return prevPendingBillingChange;
            }

            console.warn(
              "[BillingContext] Hiding non-actionable pending billing change from legacy app state:",
              {
                previousPendingBillingChange: prevPendingBillingChange,
                invoiceDetails,
                classification,
              },
            );

            return null;
          });
        }
      } catch (err) {
        if (isStale()) {
          console.log(
            "[BillingContext] Ignoring stale pending-change reconciliation error:",
            {
              requestId,
              pendingChangeForReconciliation,
              error: err?.message || err,
            },
          );
          return;
        }

        console.error(
          "[BillingContext] Pending-change invoice reconciliation failed:",
          err,
        );

        lastReconciledPendingInvoiceIdRef.current = null;
        lastReconciledPendingInvoiceStatusRef.current = "error";
        lastPendingReconciliationKeyRef.current = null;

        setPendingChangeState((prevPendingChangeState) => {
          if (
            !prevPendingChangeState?.pendingChange ||
            prevPendingChangeState.pendingChange.invoiceId !==
              currentPendingInvoiceId
          ) {
            return prevPendingChangeState;
          }

          return {
            ...prevPendingChangeState,
            kind: "error",
            invoiceClassification: "error",
            invoiceDetails: null,
            invoiceUrl: null,
            invoiceStatus: null,
            paymentStatus: null,
            canResumePayment: false,
          };
        });
      }
    };

    reconcilePendingInvoice();

    return () => {
      cancelled = true;
    };
  }, [
    pendingChangeState,
    getInvoiceDetails,
    classifyPendingInvoiceDetails,
  ]);

  /* ────────────────────────────────────────────────────────────────
   * Clear compatibility exports when no pending change remains
   *
   * Why:
   * - pendingBillingChange is now a legacy compatibility export.
   * - pendingChangeState is the provider-owned source of truth.
   * - We still want the legacy compatibility surface to settle back to
   *   idle/null when no pending change remains.
   *
   * ENTERPRISE STABILIZATION RULE
   * - Do NOT clear reconciliation memory here.
   *
   * Why:
   * - The provider may need to remember that a just-classified invoice was
   *   stale / terminal so that a brief Firestore re-emission of the same
   *   pending invoice does not restart preview/reconciliation loops.
   * - New pending invoices are already handled by the "new pending invoice
   *   detected" branch in the reconciliation effect.
   * ---------------------------------------------------------------- */
  useEffect(() => {
    const hasProviderOwnedPendingChange =
      !!pendingChangeState?.pendingChange;

    if (pendingBillingChange || hasProviderOwnedPendingChange) {
      return;
    }

    if (pendingChangeState?.kind !== "none") {
      setPendingChangeState(createEmptyPendingChangeState());
    }

    if (pendingBillingChangeStatus !== "idle") {
      setPendingBillingChangeStatus("idle");
    }

    if (pendingBillingChangeInvoiceDetails !== null) {
      setPendingBillingChangeInvoiceDetails(null);
    }
  }, [
    pendingBillingChange,
    pendingChangeState,
    pendingBillingChangeStatus,
    pendingBillingChangeInvoiceDetails,
  ]);
  /* ────────────────────────  context value  ───────────────────────── */
  const value = useMemo(() => ({
    /* state */
    currentSubscription,
    pendingBillingChange,
    pendingChangeState,
    pendingBillingChangeStatus,
    pendingBillingChangeInvoiceDetails,
    subscriptions,
    invoices,
    plans,
    hasValidPaymentMethod,
    billingLoaded,
    showNavUpgrade,
    setShowNavUpgrade,
    reconciling,

    /* helpers */
    redirectToCustomerPortal,
    openPortalUpdate,
    syncStripeSubscriptionDetails,
    fetchInvoices,
    getInvoiceDetails,
    cancelPendingBillingChange,
    cancelScheduledDowngrade,

    /* checkout / update */
    createPaymentIntent,
    createCheckoutSession,
    switchIntervalAndPay,
    updateSeats,
    fetchProrationDetails,
    previewBillingChange,
    cancelSubscription,
  }), [
    currentSubscription,
    pendingBillingChange,
    pendingChangeState,
    pendingBillingChangeStatus,
    pendingBillingChangeInvoiceDetails,
    subscriptions,
    invoices,
    plans,
    hasValidPaymentMethod,
    billingLoaded,
    redirectToCustomerPortal,
    openPortalUpdate,
    syncStripeSubscriptionDetails,
    fetchInvoices,
    getInvoiceDetails,
    cancelPendingBillingChange,
    cancelScheduledDowngrade,
    createPaymentIntent,
    createCheckoutSession,
    switchIntervalAndPay,
    updateSeats,
    fetchProrationDetails,
    previewBillingChange,
    cancelSubscription,
    showNavUpgrade,
    setShowNavUpgrade,
    reconciling,
  ]);

  /* ----------------------------------------------------------------- */
  return (
    <BillingsContext.Provider value={value}>
      {children}
    </BillingsContext.Provider>
  );
};

export default BillingsProvider;
